import { useRef, useState } from "react";
import { Send, Mic, Square, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { transcribeChatAudio } from "../../lib/api";
import { useRecorder } from "./useRecorder";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

// Text + push-to-talk mic + photo attach. Voice fills the input; photos go straight to onPhoto(file).
export const ChatComposer = ({ value, onChange, onSend, onPhoto, busy, placeholder, allowPhoto = true }) => {
  const { recording, start, stop, supported } = useRecorder();
  const [transcribing, setTranscribing] = useState(false);
  const fileRef = useRef(null);

  const toggleMic = async () => {
    if (recording) {
      const blob = await stop();
      if (!blob || blob.size < 1000) return;
      setTranscribing(true);
      try {
        const { text } = await transcribeChatAudio(blob);
        if (text) onChange(value ? `${value} ${text}` : text);
        else toast.message("I didn't catch that — try again a little closer to the mic");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't transcribe that");
      } finally {
        setTranscribing(false);
      }
      return;
    }
    try { await start(); } catch { toast.error("Microphone access was blocked"); }
  };

  return (
    <div className="p-3 border-t border-border flex items-center gap-1.5">
      {allowPhoto && (
        <>
          <input ref={fileRef} data-testid="chat-photo-input" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onPhoto(f); }} />
          <Button data-testid="chat-photo-btn" type="button" variant="ghost" size="icon" disabled={busy} onClick={() => fileRef.current?.click()} title="Snap a document and ask about it" className="shrink-0 text-muted-foreground hover:text-primary"><Camera className="h-4 w-4" /></Button>
        </>
      )}
      <Input
        data-testid="chat-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSend()}
        placeholder={recording ? "Listening… tap the square when you're done" : transcribing ? "Transcribing…" : placeholder}
        className="bg-background"
      />
      {supported && (
        <Button data-testid="chat-mic-btn" type="button" variant={recording ? "default" : "ghost"} size="icon" disabled={busy || transcribing} onClick={toggleMic} title={recording ? "Stop recording" : "Ask by voice"} className={`shrink-0 ${recording ? "bg-rose-600 hover:bg-rose-600 text-white animate-pulse" : "text-muted-foreground hover:text-primary"}`}>
          {transcribing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
        </Button>
      )}
      <Button data-testid="chat-send-btn" onClick={onSend} disabled={busy || recording} size="icon" className="bg-primary hover:bg-primary/90 shrink-0"><Send className="h-4 w-4" /></Button>
    </div>
  );
};
